import { usePriorityRepository } from "@/shared/contexts/repository-context";
import type { PriorityDto } from "@/spz-api/model";
import { getGetPriorityOptionsQueryKey } from "@/spz-api/priorities/priorities";
import { Icon } from "@spz/ui/components/icon";
import { PriorityBadge } from "@spz/ui/components/priority-badge";
import type { OptionRendererProps } from "@spz/ui/components/searchable-select";
import { SearchableSelect } from "@spz/ui/components/searchable-select";
import { cn } from "@spz/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import type { FC } from "react";

export interface PriorityOption {
  value: string;
  label: string;
  variant: string;
  symbol: string;
}
type PrioritySelectorProps = {
  onChange: (priority: PriorityOption) => void;
  value: string | null;
  initialValue?: PriorityDto | null;
  disabled?: boolean;
  placeholder?: string;
  readonly?: boolean;
  isBulkSelection?: boolean;
  className?: string;
};

const PriorityOptionRenderer = ({
  option,
  isSelected,
  readonly,
}: OptionRendererProps<string>) => {
  const priorityOption = option as PriorityOption;

  return (
    <div
      className="flex items-center justify-between w-full"
      style={{
        maxWidth: "calc(var(--radix-popover-trigger-width) - 16px)",
      }}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <PriorityBadge
          id={priorityOption.value}
          variant={priorityOption.variant}
          symbol={priorityOption.symbol}
          label={priorityOption.label}
          showLabel
        />
        {!readonly && isSelected && (
          <Icon icon="check" className="text-primary" />
        )}
      </div>
    </div>
  );
};

export const PrioritySelector: FC<PrioritySelectorProps> = ({
  onChange,
  value,
  initialValue,
  disabled,
  placeholder,
  readonly = false,
  isBulkSelection = false,
  className,
}) => {
  const priorityRepository = usePriorityRepository();
  const { data: priorities, isLoading } = useQuery({
    queryKey: getGetPriorityOptionsQueryKey(),
    queryFn: () => priorityRepository.getPriorityOptions(),
  });

  const effectivePlaceholder = placeholder ?? t("change_priority");

  const effectiveValue = isBulkSelection
    ? undefined
    : value
      ? [value]
      : undefined;

  const effectiveDefaultValue = isBulkSelection
    ? undefined
    : initialValue?.id
      ? [initialValue.id]
      : undefined;

  return (
    <SearchableSelect<string>
      onSelect={(ids) => {
        const selectedId = ids[0];
        const selectedOption = priorities?.find(
          (opt) => opt.value === selectedId,
        );
        if (selectedOption) {
          onChange(selectedOption);
        }
      }}
      defaultValue={effectiveDefaultValue}
      value={effectiveValue}
      disabled={disabled}
      placeholder={effectivePlaceholder}
      options={priorities}
      isLoading={isLoading}
      className={cn(
        "min-w-[200px] max-w-md",
        readonly && "border-none !cursor-not-allowed",
        className,
      )}
      size="small"
      readonly={readonly}
      searchPlaceholder={t("search")}
      noResultsText={t("no_priorities_found")}
      optionRenderer={PriorityOptionRenderer}
    />
  );
};
